import { CommandBarButton, Dialog, Stack } from "@fluentui/react";
import classnames from "classnames";
import MediaQuery from "react-responsive";
import SlidingPane from "react-sliding-pane";
import { CloseIcon } from "../../assets/closeIcon";
import styles from "../../pages/chat/Chat.module.css";

export type RemoveProgramConfirm = { code: string; label: string } | null;
export type RemoveCaseConfirm = { number: string; label: string } | null;

interface ProfileRemoveConfirmDialogsProps {
    removeProgramConfirm: RemoveProgramConfirm;
    removeCaseConfirm: RemoveCaseConfirm;
    isDarkMode: boolean;
    branding?: any;
    modalProps: any;
    onCancelProgramRemove: () => void;
    onConfirmProgramRemove: (programCode: string) => void;
    onCancelCaseRemove: () => void;
    onConfirmCaseRemove: (caseNumber: string) => void;
}

interface RemoveConfirmDialogProps {
    title: string;
    message: string;
    isOpen: boolean;
    isDarkMode: boolean;
    branding?: any;
    modalProps: any;
    onCancel: () => void;
    onConfirm: () => void;
}

const closeXModalProfile = classnames(styles.modalProfileClose, "notranslate");

const RemoveConfirmDialog = ({
    title,
    message,
    isOpen,
    isDarkMode,
    branding,
    modalProps,
    onCancel,
    onConfirm,
}: RemoveConfirmDialogProps) => (
    <>
        <MediaQuery query="(min-width: 992px)">
            <Dialog
                data-theme={isDarkMode ? "dark" : ""}
                hidden={!isOpen}
                onDismiss={onCancel}
                dialogContentProps={{ title: "", showCloseButton: true }}
                modalProps={modalProps}
                styles={{ main: styles.confirmDialogShell }}
            >
                <Stack horizontal verticalAlign="center" horizontalAlign="baseline" data-theme={isDarkMode ? "dark" : ""}>
                    <div className={styles.dialogTitle} data-theme={isDarkMode ? "dark" : ""}>{title}</div>
                </Stack>
                <Stack horizontal verticalAlign="center" horizontalAlign="baseline" data-theme={isDarkMode ? "dark" : ""}>
                    <div className={styles.dialogContent} data-theme={isDarkMode ? "dark" : ""}>{message}</div>
                </Stack>
                <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 8 }} className={styles.confirmDialogActions}>
                    <CommandBarButton
                        role="button"
                        className={styles.submitLoginButtonAuth}
                        text="Cancel"
                        aria-label="Cancel"
                        title="Cancel"
                        onClick={onCancel}
                    />
                    <CommandBarButton
                        role="button"
                        className={styles.submitLoginButtonAuth}
                        text="Remove"
                        aria-label="Remove"
                        title="Remove"
                        onClick={onConfirm}
                    />
                </Stack>
            </Dialog>
        </MediaQuery>
        <MediaQuery query="(max-width: 991px)">
            {isOpen && (
                <SlidingPane
                    className={`${styles.spCustomProfile} auth-slide-pane`}
                    isOpen={isOpen}
                    from="bottom"
                    width="100%"
                    hideHeader
                    onRequestClose={onCancel}
                >
                    <div className={styles.profileModalMain} data-theme={isDarkMode ? "dark" : ""}>
                        <Stack className={styles.modalContainer} data-theme={isDarkMode ? "dark" : ""}>
                            <Stack horizontal verticalAlign="center" className={styles.modalProfileHeader} data-theme={isDarkMode ? "dark" : ""}>
                                <Stack horizontalAlign="center" verticalAlign="center" style={{ display: "block", margin: "0 auto" }}>
                                    <img src={isDarkMode ? branding?.dialog_logo_darkmode : branding?.dialog_logo} aria-hidden="true" tabIndex={-1} alt="Brand Logo" className={styles.modalProfileIcon} />
                                </Stack>
                                <div
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Close"
                                    title="Close"
                                    onClick={onCancel}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onCancel(); }}
                                    className={closeXModalProfile}
                                >
                                    <CloseIcon fill="var(--YW_SignIn_Modal_Close_Icon_Color)" />
                                </div>
                            </Stack>
                            <Stack tabIndex={0} horizontalAlign="start" verticalAlign="center" className={styles.modalProfileSubHeader}>
                                {title}
                            </Stack>
                            <Stack tabIndex={0} horizontalAlign="start" verticalAlign="center" style={{ padding: "10px 0" }}>
                                {message}
                            </Stack>
                            <Stack horizontal horizontalAlign="start" tokens={{ childrenGap: 8 }}>
                                <CommandBarButton
                                    role="button"
                                    className={styles.submitLoginButtonAuth}
                                    text="Cancel"
                                    aria-label="Cancel"
                                    title="Cancel"
                                    onClick={onCancel}
                                />
                                <CommandBarButton
                                    role="button"
                                    className={styles.submitLoginButtonAuth}
                                    text="Remove"
                                    aria-label="Remove"
                                    title="Remove"
                                    onClick={onConfirm}
                                />
                            </Stack>
                        </Stack>
                    </div>
                </SlidingPane>
            )}
        </MediaQuery>
    </>
);

const ProfileRemoveConfirmDialogs = ({
    removeProgramConfirm,
    removeCaseConfirm,
    isDarkMode,
    branding,
    modalProps,
    onCancelProgramRemove,
    onConfirmProgramRemove,
    onCancelCaseRemove,
    onConfirmCaseRemove,
}: ProfileRemoveConfirmDialogsProps) => (
    <>
        <RemoveConfirmDialog
            title="Remove Program"
            message={removeProgramConfirm ? `Are you sure you want to remove ${removeProgramConfirm.label} from your profile?` : ""}
            isOpen={removeProgramConfirm !== null}
            isDarkMode={isDarkMode}
            branding={branding}
            modalProps={modalProps}
            onCancel={onCancelProgramRemove}
            onConfirm={() => {
                if (removeProgramConfirm) onConfirmProgramRemove(removeProgramConfirm.code);
            }}
        />
        <RemoveConfirmDialog
            title="Remove Case"
            message={removeCaseConfirm ? `Are you sure you want to remove case ${removeCaseConfirm.label} from your profile?` : ""}
            isOpen={removeCaseConfirm !== null}
            isDarkMode={isDarkMode}
            branding={branding}
            modalProps={modalProps}
            onCancel={onCancelCaseRemove}
            onConfirm={() => {
                if (removeCaseConfirm) onConfirmCaseRemove(removeCaseConfirm.number);
            }}
        />
    </>
);

export default ProfileRemoveConfirmDialogs;
